import React, { useCallback, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Switch,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { observer } from "mobx-react-lite";
import { useStores } from "../../../use-stores";
import { StyledTableRow } from "../../../styles/Shipmentstyles";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import CustomTypography from "../common/form/CustomTypography";
import { useNavigate } from "react-router-dom";
import { SWAGSTORE_STEPS } from "../../../constants/SwagstoreConstants";
import AddBalanceModal, { AddBalanceSubmitPayload } from "./AddBalanceModal";
import WalletHistoryDrawer from "./WalletHistoryDrawer";
import type { Store } from "../../../models/Swagstore/StoreDetailsModel";

interface StoreTableProps {
  stores: Store[];
  maxHeight?: number;
  minHeight?: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onStatusChange?: (storeId: number | string, isActive: boolean) => void;
  canUpdateStatus?: boolean;
  canAddBalance?: boolean;
  canViewBalance?: boolean;
  canViewPurchaseTransactions?: boolean;
  onAddBalanceSuccess?: (balance: number) => void;
  onPurchaseTransactions?: (store: Store) => void;
}

const StoreTable = ({
  stores,
  maxHeight,
  page_no,
  limit,
  total_count,
  minHeight = window.innerHeight - 320,
  onPageChange,
  onRowsPerPageChange,
  onStatusChange,
  canUpdateStatus = true,
  canAddBalance = false,
  canViewBalance = false,
  canViewPurchaseTransactions = false,
  onAddBalanceSuccess,
  onPurchaseTransactions,
}: StoreTableProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { swagStoreStore } = useStores();
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [selectedStoreForActions, setSelectedStoreForActions] =
    useState<Store | null>(null);

  const canShowActions = canAddBalance || canViewPurchaseTransactions;
  const isActionsMenuOpen = Boolean(actionsAnchorEl);

  const resolveSwagstoreBaseUrl = () => {
    const host = window.location.hostname.toLowerCase();
    if (host.includes("localhost")) return "http://localhost:3001";
    if (host.includes("dev")) return "https://dev-swagstore.swageazy.com";
    if (host.includes("stage") || host.includes("staging")) {
      return "https://stage-swagstore.swageazy.com";
    }
    return "https://swagstore.swageazy.com";
  };

  const handleRowsChange = (value: number) => {
    onRowsPerPageChange(value);
  };

  const handleAddBalanceSubmit = async (payload: AddBalanceSubmitPayload) => {
    const response = await swagStoreStore.addWalletBalance(payload);
    const balance = Number(response?.result?.balance);
    swagStoreStore.closeAddBalanceModal();
    if (Number.isFinite(balance)) {
      onAddBalanceSuccess?.(balance);
    }
  };

  const formatWalletBalance = (store: Store) => {
    const balance = swagStoreStore.getStoreWalletBalance(store);
    if (balance == null) return "—";
    return `₹${balance.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  };

  const getStoreIdentifier = (store: Store) => store.id?.toString() ?? "";

  const handleStoreIdClick = useCallback(
    (storeId: number | string, themeId?: number) => {
      const url = themeId
        ? `/swagstore/create-store?store_id=${storeId}&theme_id=${themeId}&step=${SWAGSTORE_STEPS.STORE_DETAILS}`
        : `/swagstore/create-store?store_id=${storeId}&step=${SWAGSTORE_STEPS.STORE_DETAILS}`;

      navigate(url, {
        replace: false,
        state: { storeId, themeId },
      });
    },
    [navigate],
  );

  const handleActionsMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    store: Store,
  ) => {
    setActionsAnchorEl(event.currentTarget);
    setSelectedStoreForActions(store);
  };

  const handleActionsMenuClose = () => {
    setActionsAnchorEl(null);
    setSelectedStoreForActions(null);
  };

  const handleAddBalanceClick = () => {
    const store = selectedStoreForActions;
    handleActionsMenuClose();
    if (!store) return;
    const id = store.store_id ?? store.id;
    if (id != null) {
      swagStoreStore.openAddBalanceModal(id);
    }
  };

  const handlePurchaseTransactionsClick = () => {
    const store = selectedStoreForActions;
    handleActionsMenuClose();
    if (!store || !onPurchaseTransactions) return;
    onPurchaseTransactions(store);
  };

  return (
    <>
      <TableContainer sx={{ maxHeight: maxHeight, minHeight: minHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell>Store ID</AppTableHeaderCell>
              <AppTableHeaderCell>Store Name</AppTableHeaderCell>
              <AppTableHeaderCell>Customer Name</AppTableHeaderCell>
              <AppTableHeaderCell>Public</AppTableHeaderCell>
              <AppTableHeaderCell>Status</AppTableHeaderCell>
              <AppTableHeaderCell>Created At</AppTableHeaderCell>
              <AppTableHeaderCell>Updated At</AppTableHeaderCell>
              <AppTableHeaderCell>Visit Store</AppTableHeaderCell>
              {canViewBalance && (
                <AppTableHeaderCell>Wallet Balance</AppTableHeaderCell>
              )}
              {canShowActions && (
                <AppTableHeaderCell>Actions</AppTableHeaderCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {stores.map((store, index) => {
              const storeId = getStoreIdentifier(store);
              const rowKey = storeId ? `store-${storeId}` : `store-${index}`;

              return (
                <StyledTableRow key={rowKey}>
                  <TableCell
                    sx={{
                      cursor: "pointer",
                      textDecoration: "underline",
                      textAlign: "center",
                      fontSize: 14,
                    }}
                  >
                    <Box
                      onClick={() => {
                        const clickedStoreId = getStoreIdentifier(store);
                        const clickedThemeId = store.theme_id;
                        if (clickedStoreId) {
                          handleStoreIdClick(clickedStoreId, clickedThemeId);
                        }
                      }}
                    >
                      {storeId || "-"}
                    </Box>
                  </TableCell>
                  <AppTableRowCell>{store.store_name || "-"}</AppTableRowCell>
                  <AppTableRowCell>
                    {store.customer_name || "-"}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {(() => {
                      const isPublic = store.is_public as any;
                      if (isPublic === undefined || isPublic === null)
                        return "No";
                      if (
                        isPublic === false ||
                        isPublic === 0 ||
                        isPublic === "0"
                      )
                        return "No";
                      return "Yes";
                    })()}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {onStatusChange && canUpdateStatus ? (
                      <Tooltip
                        title={
                          Boolean(store.is_active)
                            ? "Click to deactivate"
                            : "Click to activate"
                        }
                      >
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const storeIdToUpdate = store.store_id ?? store.id;
                            if (storeIdToUpdate != null) {
                              const newStatus = !Boolean(store.is_active);
                              onStatusChange(storeIdToUpdate, newStatus);
                            }
                          }}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            cursor:
                              store.store_id != null || store.id != null
                                ? "pointer"
                                : "default",
                          }}
                        >
                          <Switch
                            checked={Boolean(store.is_active)}
                            size="small"
                            color="primary"
                            disabled={
                              store.store_id == null && store.id == null
                            }
                            sx={{ pointerEvents: "none" }}
                          />
                          <Box component="span" sx={{ fontSize: "0.813rem" }}>
                            {Boolean(store.is_active) ? "Active" : "Inactive"}
                          </Box>
                        </Box>
                      </Tooltip>
                    ) : Boolean(store.is_active) ? (
                      "Active"
                    ) : (
                      "Inactive"
                    )}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {store.created_at
                      ? new Date(store.created_at).toLocaleString()
                      : "-"}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {store.updated_at
                      ? new Date(store.updated_at).toLocaleString()
                      : "-"}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    <a
                      href={`${resolveSwagstoreBaseUrl()}?store_id=${store.store_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Visit Store
                    </a>
                  </AppTableRowCell>
                  {canViewBalance && (
                    <AppTableRowCell>
                      {formatWalletBalance(store)}
                    </AppTableRowCell>
                  )}
                  {canShowActions && (
                    <AppTableRowCell sx={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        onClick={(event) => handleActionsMenuOpen(event, store)}
                        aria-label="Store actions"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </AppTableRowCell>
                  )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        fontSize="0.8rem"
        pageNo={page_no}
        limit={limit}
        totalCount={total_count}
        onPageChange={onPageChange}
        handleRowsChange={handleRowsChange}
      />

      {canAddBalance && (
        <AddBalanceModal
          open={swagStoreStore.addBalanceStoreId != null}
          storeId={swagStoreStore.addBalanceStoreId ?? undefined}
          onClose={() => swagStoreStore.closeAddBalanceModal()}
          onSubmit={handleAddBalanceSubmit}
        />
      )}

      {canViewBalance && (
        <WalletHistoryDrawer
          open={swagStoreStore.walletHistoryStoreId != null}
          onClose={() => swagStoreStore.closeWalletHistoryDrawer()}
        />
      )}

      {canShowActions && (
        <Menu
          anchorEl={actionsAnchorEl}
          open={isActionsMenuOpen}
          onClose={handleActionsMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          disableAutoFocusItem
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                boxShadow:
                  "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                minWidth: 220,
                overflow: "hidden",
              },
            },
          }}
        >
          {canAddBalance ? (
            <MenuItem
              onClick={handleAddBalanceClick}
              sx={{
                fontSize: "0.9rem",
                px: 2,
                py: 1.25,
                gap: 1,
                "&:hover": {
                  backgroundColor:
                    theme.palette.customColors?.lightPurple ||
                    theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="body2" fontWeight={600}>
                Add Balance
              </CustomTypography>
            </MenuItem>
          ) : null}
          {canViewPurchaseTransactions ? (
            <MenuItem
              onClick={handlePurchaseTransactionsClick}
              sx={{
                fontSize: "0.9rem",
                px: 2,
                py: 1.25,
                gap: 1,
                borderTop: canAddBalance
                  ? `1px solid ${theme.palette.divider}`
                  : undefined,
                "&:hover": {
                  backgroundColor:
                    theme.palette.customColors?.lightPurple ||
                    theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="body2" fontWeight={600}>
                Purchase Transactions
              </CustomTypography>
            </MenuItem>
          ) : null}
        </Menu>
      )}
    </>
  );
};

export default observer(StoreTable);
